/**
 * useExtractedResume — jobs matching the skills extracted from an uploaded resume (/search_by_resume).
 * Mirrors Flutter ExtractedResumeBloc (lib/application/extracted_resume/extracted_resume_bloc.dart):
 *   _Init → _SetSearchInput (top 3 resume skills + the visitor's country) → _SetPredefinedFilters
 *   → _OnApplyFilters (search + aggregations); search bar Go, filters, sort, save / unsave,
 *   load more, save-after-login, language change (_OnLanguageChange re-runs the search), and the
 *   card / detail-panel actions (onTapTitle, onApply).
 *
 * The extraction result is put in sessionStorage by Home (`extractedResumeData`), so the page
 * survives the login round trip of an anonymous save.
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '@/config/firebase';
import { useAuthStore } from '@/store/authStore';
import { useLocalizationStore } from '@/store/localizationStore';
import {
  searchJobs,
  getSearchAggregations,
  getAppliedSavedJobs,
  getSavedJobsCount,
  saveJob,
  unsaveJob,
} from '@/services/api/homeApi';
import { getUserDetails } from '@/services/api/authApi';
import type { CandidateDetails } from '@/services/api/authApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import type { LoginReturnInput } from '@/services/auth/postLoginRedirect';
import { getMyCountryName } from '@/utils/myCountry';
import { ROUTES } from '@/app/router/routes';
import {
  readAppliedSavedCache,
  withAppliedSaved,
  writeAppliedSavedCache,
} from '@/features/home/utils/appliedSavedCache';
import { APPLICATION_PAGE, JOB_SHARE_REF, setApplicationPageAndType } from '../utils/jobOrigin';
import {
  applyFilterSelection,
  defaultSortFilters,
  hasSearchTerms,
  sortSelectedOnTop,
  withCheckedState,
  type SelectedFilters,
} from '../utils/filterState';
import type {
  Aggregation,
  AggregationValue,
  JobEntity,
  SearchInput,
} from '@/types/job.types';
import { setRelevanceData } from '@/features/jobs/utils/relevanceData';

/** Flutter ExtractedResumeBloc.MAX_SEARCH_SKILLS — resume skills searched initially. */
const MAX_SEARCH_SKILLS = 3;
/** Flutter ExtractedResumeContainer.maxSkillsToShow */
export const MAX_RESUME_SKILLS_SHOWN = 20;
const PAGE_SIZE = 6;
const ANALYTICS_LOCATION = 'Extracted Resume';
/** Flutter NavigationService analyticsClass for this page */
const SCREEN_CLASS = 'uploaded_resume_page';
export const EXTRACTED_RESUME_KEY = 'extractedResumeData';

// Toast texts — Flutter ErrorConstants
const SAVE_JOB_SUCCESS = 'Saved the job successfully';
const SAVE_JOB_FAILED = 'Failed to save the job!';
const UNSAVE_JOB_SUCCESS = 'Unsaved the job successfully';
const UNSAVE_JOB_FAILED = 'Failed to unsave the job!';

export interface ExtractedResume {
  fileName: string;
  /** Flutter extractedResumeSkills — from `skillExpertise`. */
  skills: string[];
  /** Flutter shows the resume block when skillDetails or personalDetails.location exist. */
  showDetails: boolean;
}

interface Toast {
  message: string;
  type: 'success' | 'error';
}

/** The extraction result stored by Home, or null when the page is opened without one. */
function readExtractedResume(): ExtractedResume | null {
  try {
    const data = JSON.parse(sessionStorage.getItem(EXTRACTED_RESUME_KEY) ?? 'null') as Record<string, unknown> | null;
    if (!data) return null;
    const list = (key: string) => (Array.isArray(data[key]) ? (data[key] as Array<{ skill?: unknown }>) : []);
    const skills = list('skillExpertise')
      .map((s) => String(s?.skill ?? '').trim())
      .filter(Boolean);
    const location = String((data.personalDetails as { location?: unknown } | undefined)?.location ?? '');
    return {
      fileName: String(data.fileName ?? ''),
      skills,
      showDetails: list('skillDetails').length > 0 || location.length > 0,
    };
  } catch {
    return null;
  }
}

/** Flutter _SetSearchInput */
function initialSearchInput(skills: string[]): SearchInput {
  return {
    skills: skills.join(','),
    roles: '',
    locations: getMyCountryName(),
    switchIndex: 0,
    listOfSearchSkills: skills,
  };
}

export function useExtractedResume() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const currentLanguage = useLocalizationStore((s) => s.currentLanguage);
  // Set by login/MFA after an anonymous save (Flutter ProfileInput.saveJobInProgress)
  const saveJobInProgress = !!(location.state as { profileInput?: LoginReturnInput } | null)?.profileInput
    ?.saveJobInProgress;

  const [resume] = useState(readExtractedResume);
  const [initialSkills] = useState(() => (resume?.skills ?? []).slice(0, MAX_SEARCH_SKILLS));
  /** Skills currently in the search bar (resume chips add to it). */
  const [barSkills, setBarSkills] = useState<string[]>(initialSkills);
  const [searchInput, setSearchInput] = useState<SearchInput>(() => initialSearchInput(initialSkills));
  const [isResumeSkills, setIsResumeSkills] = useState(initialSkills.length > 0);

  const [entities, setEntities] = useState<JobEntity[]>([]);
  const [rawAggregations, setRawAggregations] = useState<Aggregation[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [candidateDetails, setCandidateDetails] = useState<CandidateDetails | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const requestRef = useRef(0);
  const saveAfterLoginDone = useRef(false);

  // Checkboxes / sort follow the selected filters (Flutter _SetFilterBackup)
  const aggregations = useMemo(
    () => withCheckedState(rawAggregations, selectedFilters),
    [rawAggregations, selectedFilters],
  );

  /* ── Search ── */

  /** Flutter _GetSavedAppliedJobs — the cached statuses are applied with the results; this applies the network result. */
  const refreshAppliedSaved = useCallback(async (requestId: number) => {
    if (!auth.currentUser) return;
    try {
      const data = await getAppliedSavedJobs();
      writeAppliedSavedCache(data);
      if (requestId === requestRef.current) setEntities((prev) => withAppliedSaved(prev, data));
    } catch {
      // The cached flags stay
    }
  }, []);

  /** Flutter _OnApplyFilters → _GetSearchResults + _GetSearchResultAggregation */
  const runSearch = useCallback(
    async (input: SearchInput, filters: SelectedFilters) => {
      const updated: SearchInput = { ...input, filters };
      const requestId = ++requestRef.current;
      setSearchInput(updated);
      setCurrentPage(1);
      setIsLoading(true);

      getSearchAggregations(updated)
        .then((res) => {
          if (requestId === requestRef.current) setRawAggregations(sortSelectedOnTop(res.aggregations ?? [], filters));
        })
        .catch(() => {});

      try {
        const res = await searchJobs(updated, 1, PAGE_SIZE);
        if (requestId !== requestRef.current) return;
        const total = res.total ?? 0;
        setEntities(withAppliedSaved(res.entities ?? [], readAppliedSavedCache()));
        setTotalCount(total);
        setHasMore(total > PAGE_SIZE);
        void refreshAppliedSaved(requestId);
      } catch {
        if (requestId !== requestRef.current) return;
        setEntities([]);
        setTotalCount(0);
        setHasMore(false);
      } finally {
        if (requestId === requestRef.current) setIsLoading(false);
      }
    },
    [refreshAppliedSaved],
  );

  /** Flutter _OnLoadMore */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading || isLoadingMore) return;
    const requestId = requestRef.current;
    const nextPage = currentPage + 1;
    setIsLoadingMore(true);
    try {
      const res = await searchJobs(searchInput, nextPage, PAGE_SIZE);
      if (requestId !== requestRef.current) return;
      const list = withAppliedSaved(res.entities ?? [], readAppliedSavedCache());
      setEntities((prev) => [...prev, ...list]);
      setCurrentPage(nextPage);
      setHasMore(nextPage < Math.ceil((res.total ?? 0) / PAGE_SIZE));
      void refreshAppliedSaved(requestId);
    } catch {
      if (requestId === requestRef.current) setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoading, isLoadingMore, currentPage, searchInput, refreshAppliedSaved]);

  /* ── Init — Flutter _Init → _SetSearchInput → _SetPredefinedFilters ── */
  useEffect(() => {
    const filters = defaultSortFilters(initialSkills.length > 0);
    setSelectedFilters(filters);
    void runSearch(initialSearchInput(initialSkills), filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Language change — Flutter _OnLanguageChange re-runs the search ── */
  const languageRef = useRef(currentLanguage);
  useEffect(() => {
    if (languageRef.current === currentLanguage) return;
    languageRef.current = currentLanguage;
    void runSearch(searchInput, selectedFilters);
  }, [currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Infinite scroll — Flutter loads the next page 600px before the end ── */
  useEffect(() => {
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollHeight - scrollTop - clientHeight <= 600) void loadMore();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [loadMore]);

  /* ── Signed in: candidate, saved-jobs count, save after login — Flutter _GetUserDetails ── */
  const completeSaveAfterLogin = useCallback(
    async (details: CandidateDetails) => {
      const saved = localStorage.getItem('saveJobDetails');
      if (!saved || !auth.currentUser) return;
      try {
        const job = JSON.parse(saved) as Record<string, unknown>;
        if (!job.requisitionId) return;
        await saveJob({
          candidateId: (details as Record<string, unknown>).id,
          requisitionId: job.requisitionId,
          candidateDisplayId: (details as Record<string, unknown>).displayId,
          requisitionDisplayId: job.requisitionDisplayId,
          primaryEmailId: details.primaryEmailId,
          exprncFromMnthInYears: job.exprncFromMnthInYears,
          exprncToMnthInYears: job.exprncToMnthInYears,
        });
        localStorage.removeItem('saveJobDetails');
        setEntities((prev) =>
          prev.map((e) => (e.displayId === job.requisitionDisplayId ? { ...e, isSavedJob: true } : e)),
        );
        setToast({ message: t(SAVE_JOB_SUCCESS), type: 'success' });
      } catch {
        setToast({ message: t(SAVE_JOB_FAILED), type: 'error' });
      }
      void getSavedJobsCount().catch(() => {});
    },
    [t],
  );

  useEffect(() => {
    if (!auth.currentUser) return;
    getUserDetails()
      .then((details) => {
        setCandidateDetails(details);
        if (saveJobInProgress && !saveAfterLoginDone.current) {
          saveAfterLoginDone.current = true;
          void completeSaveAfterLogin(details);
        }
      })
      .catch(() => {});
    if (!saveJobInProgress) void getSavedJobsCount().catch(() => {});
  }, [user, saveJobInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Search bar, resume skills, filters ── */

  /** Flutter ExtractedResumeContainer skill tap — adds the skill to the search bar (search runs on Go). */
  const addResumeSkill = useCallback((skill: string) => {
    setBarSkills((prev) => (prev.includes(skill) ? prev : [...prev, skill]));
  }, []);

  /** Flutter _OnClickGo — sort resets to the default for the new terms. */
  const search = useCallback(
    (input: SearchInput) => {
      const skills = input.listOfSearchSkills ?? [];
      const allFromResume = skills.every((s) => resume?.skills.includes(s));
      setIsResumeSkills(skills.length > 0 && allFromResume);
      void analyticsService.logJobSearch({ searchTerm: 'job search', filters: selectedFilters });
      const filters = defaultSortFilters(hasSearchTerms(input));
      setSelectedFilters(filters);
      void runSearch(input, filters);
    },
    [resume, selectedFilters, runSearch],
  );

  const onFilterSelection = useCallback((values: AggregationValue[]) => {
    setSelectedFilters((prev) => applyFilterSelection(prev, values));
  }, []);

  const applyFilters = useCallback(() => {
    void analyticsService.logJobSearch({ searchTerm: 'apply filter', filters: selectedFilters });
    void runSearch(searchInput, selectedFilters);
  }, [searchInput, selectedFilters, runSearch]);

  /** Sort is applied as soon as it is picked. */
  const selectSort = useCallback(
    (value: AggregationValue) => {
      const filters = applyFilterSelection(selectedFilters, [value]);
      setSelectedFilters(filters);
      void analyticsService.logJobSearch({ searchTerm: 'apply filter', filters });
      void runSearch(searchInput, filters);
    },
    [searchInput, selectedFilters, runSearch],
  );

  /** Flutter _OnClearFilter — removes one category; the search reruns on Apply. */
  const clearFilter = useCallback((key: string) => {
    setSelectedFilters((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  /** Flutter _ClearFilters → _SetPredefinedFilters */
  const clearFilters = useCallback(() => {
    const filters = defaultSortFilters(hasSearchTerms(searchInput));
    setSelectedFilters(filters);
    void runSearch({ ...searchInput, filters: undefined }, filters);
    void analyticsService.logEvent('extracted_resume', 'clear_filter');
  }, [searchInput, runSearch]);

  /* ── Save / unsave — Flutter _OnClickSaveJob / _OnClickUnsaveJob ── */

  const patchEntity = useCallback((index: number, patch: Partial<JobEntity>) => {
    setEntities((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  }, []);

  const saveJobAt = useCallback(
    async (entity: JobEntity, index: number) => {
      if (!auth.currentUser) {
        localStorage.setItem(
          'saveJobDetails',
          JSON.stringify({
            requisitionId: entity.id,
            requisitionDisplayId: entity.displayId,
            exprncFromMnthInYears: entity.requiredExperienceInMonths?.from,
            exprncToMnthInYears: entity.requiredExperienceInMonths?.to,
          }),
        );
        void analyticsService.logScreenView({ screenName: ROUTES.LOGIN });
        navigate(ROUTES.LOGIN, { state: { saveJobInProgress: true, previousPage: ROUTES.EXTRACTED_RESUME } });
        return;
      }
      patchEntity(index, { isLoadingForSaveJobButton: true });
      try {
        const details = candidateDetails ?? (await getUserDetails());
        if (!candidateDetails) setCandidateDetails(details);
        await saveJob({
          candidateId: (details as Record<string, unknown>).id,
          requisitionId: entity.id,
          candidateDisplayId: (details as Record<string, unknown>).displayId,
          requisitionDisplayId: entity.displayId,
          primaryEmailId: details.primaryEmailId,
          exprncFromMnthInYears: entity.requiredExperienceInMonths?.from,
          exprncToMnthInYears: entity.requiredExperienceInMonths?.to,
        });
        void analyticsService.logJobSave({
          location: ANALYTICS_LOCATION,
          jobId: entity.id ?? '',
          jobTitle: entity.jobTitle ?? '',
        });
        patchEntity(index, { isSavedJob: true, isLoadingForSaveJobButton: false });
        setToast({ message: t(SAVE_JOB_SUCCESS), type: 'success' });
      } catch {
        patchEntity(index, { isLoadingForSaveJobButton: false });
        setToast({ message: t(SAVE_JOB_FAILED), type: 'error' });
      }
      void getSavedJobsCount().catch(() => {});
    },
    [candidateDetails, navigate, patchEntity, t],
  );

  const unsaveJobAt = useCallback(
    async (entity: JobEntity, index: number) => {
      patchEntity(index, { isLoadingForSaveJobButton: true });
      try {
        await unsaveJob(entity.displayId ?? '');
        patchEntity(index, { isSavedJob: false, isLoadingForSaveJobButton: false });
        setToast({ message: t(UNSAVE_JOB_SUCCESS), type: 'success' });
      } catch {
        patchEntity(index, { isLoadingForSaveJobButton: false });
        setToast({ message: t(UNSAVE_JOB_FAILED), type: 'error' });
      }
      void analyticsService.logJobUnsave({
        location: ANALYTICS_LOCATION,
        jobId: entity.id ?? '',
        jobTitle: entity.jobTitle ?? '',
      });
      void getSavedJobsCount().catch(() => {});
    },
    [patchEntity, t],
  );

  /** Flutter delete-resume confirm → Home. */
  const deleteResume = useCallback(() => {
    sessionStorage.removeItem(EXTRACTED_RESUME_KEY);
    void analyticsService.logScreenView({ screenName: ROUTES.HOME });
    navigate(ROUTES.HOME, { replace: true });
  }, [navigate]);

  /* ── Card / detail-panel actions — Flutter ExtractedResumeView JobCard callbacks ── */

  /** Flutter onTapTitle — relevance for the job page, then the job in a new tab. */
  const openJob = useCallback((entity: JobEntity) => {
    setApplicationPageAndType(APPLICATION_PAGE.CAREER_RECOMMENDATION, true);
    setRelevanceData(entity);
    void analyticsService.logScreenView({ screenName: ROUTES.JOBS });
    window.open(`${ROUTES.JOBS}/${entity.displayId}?ref=${JOB_SHARE_REF.INTERNAL}`, '_blank');
  }, []);

  /** Flutter onApply — the application form (or Easy Apply) in a new tab. */
  const applyToJob = useCallback((entity: JobEntity, isEasyApply: boolean) => {
    setApplicationPageAndType(APPLICATION_PAGE.CAREER_RECOMMENDATION, true);
    const route = isEasyApply ? ROUTES.EASY_APPLY : ROUTES.APPLICATION;
    void analyticsService.logScreenView({ screenName: route, screenClass: SCREEN_CLASS });
    window.open(`${route}/${entity.displayId}`, '_blank');
  }, []);

  const clearToast = useCallback(() => setToast(null), []);

  return {
    state: {
      resume,
      barSkills,
      searchInput,
      isResumeSkills,
      entities,
      aggregations,
      selectedFilters,
      totalCount,
      hasMore,
      isLoading,
      isLoadingMore,
      toast,
    },
    actions: {
      setBarSkills,
      addResumeSkill,
      search,
      onFilterSelection,
      applyFilters,
      selectSort,
      clearFilter,
      clearFilters,
      loadMore,
      saveJob: saveJobAt,
      unsaveJob: unsaveJobAt,
      deleteResume,
      openJob,
      applyToJob,
      clearToast,
    },
  };
}
